import { useEffect } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import { filterRealCrawlNodeIds } from '../../crawlUrlTree/buildCrawlUrlTree';
import { resolveApiErrorMessage } from '../../../query/apiErrorMessage';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type {
  CrawlJob,
  CrawlSchedule,
  WithResourceId,
} from '../../../types/knowledge';
import {
  useRunKnowledgeCrawlWithSelectionMutation,
  useUpdateKnowledgeCrawlSelectionMutation,
} from '../mutations/useKnowledgeCrawlMutations';
import { useKnowledgeCrawlUrlNodesQuery } from '../queries/useKnowledgeCrawlUrlNodesQuery';
import { useKnowledgeCrawlsTabStore } from '../stores/useKnowledgeCrawlsTabStore';

type UseCrawlSelectionOptions = {
  databaseId: string;
  crawlScheduleByJobId: Map<string, WithResourceId<CrawlSchedule>>;
  onCrawlContinued?: (crawlId: string) => void;
};

export function useCrawlSelection({
  databaseId,
  crawlScheduleByJobId,
  onCrawlContinued,
}: UseCrawlSelectionOptions) {
  const { translate } = useI18n();
  const runCrawlWithSelectionMutation =
    useRunKnowledgeCrawlWithSelectionMutation();
  const updateCrawlSelectionMutation =
    useUpdateKnowledgeCrawlSelectionMutation();

  const selectionCrawl = useKnowledgeCrawlsTabStore(
    (state) => state.selectionCrawl,
  );
  const selectedNodeIds = useKnowledgeCrawlsTabStore(
    (state) => state.selectedNodeIds,
  );
  const selectionNodes = useKnowledgeCrawlsTabStore(
    (state) => state.selectionNodes,
  );
  const isSelectionSubmitting = useKnowledgeCrawlsTabStore(
    (state) => state.isSelectionSubmitting,
  );
  const openCrawlSelection = useKnowledgeCrawlsTabStore(
    (state) => state.openCrawlSelection,
  );
  const setSelectionData = useKnowledgeCrawlsTabStore(
    (state) => state.setSelectionData,
  );
  const closeCrawlSelection = useKnowledgeCrawlsTabStore(
    (state) => state.closeCrawlSelection,
  );
  const setSelectedNodeIds = useKnowledgeCrawlsTabStore(
    (state) => state.setSelectedNodeIds,
  );
  const setIsSelectionSubmitting = useKnowledgeCrawlsTabStore(
    (state) => state.setIsSelectionSubmitting,
  );

  const crawlUrlNodesQuery = useKnowledgeCrawlUrlNodesQuery({
    databaseId,
    crawlId: selectionCrawl?.id,
    enabled: selectionCrawl !== null && selectionNodes.length === 0,
  });

  const hasActiveSchedule =
    selectionCrawl != null &&
    crawlScheduleByJobId.get(selectionCrawl.id)?.active === true;

  /** Manual continue is only for human awaiting — not the brief scheduled-run flash. */
  const isAwaitingSelectionStart =
    selectionCrawl?.status === 'AWAITING_SELECTION' && !hasActiveSchedule;

  const isScheduledAwaitingSelection =
    selectionCrawl?.status === 'AWAITING_SELECTION' && hasActiveSchedule;

  useEffect(() => {
    if (!crawlUrlNodesQuery.data || selectionNodes.length > 0) {
      return;
    }

    setSelectionData(crawlUrlNodesQuery.data);
  }, [crawlUrlNodesQuery.data, selectionNodes.length, setSelectionData]);

  useEffect(() => {
    if (!crawlUrlNodesQuery.isError || selectionCrawl === null) {
      return;
    }

    void resolveApiErrorMessage(crawlUrlNodesQuery.error).then((apiMessage) => {
      toast.error({
        title: translate('knowledge.crawlSelectionLoadError'),
        description: apiMessage,
      });
      closeCrawlSelection();
    });
  }, [
    closeCrawlSelection,
    crawlUrlNodesQuery.error,
    crawlUrlNodesQuery.isError,
    selectionCrawl,
    translate,
  ]);

  const openSelection = (crawl: WithResourceId<CrawlJob>) => {
    openCrawlSelection(crawl);
  };

  const handleCloseSelection = () => {
    if (isSelectionSubmitting) {
      return;
    }
    closeCrawlSelection();
  };

  const runSelectedCrawl = () => {
    if (!selectionCrawl || !isAwaitingSelectionStart) {
      return;
    }

    const realSelectedNodeIds = filterRealCrawlNodeIds(selectedNodeIds);
    if (realSelectedNodeIds.length === 0) {
      return;
    }

    setIsSelectionSubmitting(true);
    runCrawlWithSelectionMutation.mutate(
      {
        databaseId,
        crawlId: selectionCrawl.id,
        selectedNodeIds: realSelectedNodeIds,
      },
      {
        onSuccess: (_data, variables) => {
          toast.success({
            title: translate('knowledge.crawlSelectionStartedTitle'),
            description: '',
          });
          onCrawlContinued?.(variables.crawlId);
          closeCrawlSelection();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlSelectionStartError'),
          );
        },
        onSettled: () => {
          setIsSelectionSubmitting(false);
        },
      },
    );
  };

  const saveSelection = () => {
    if (!selectionCrawl) {
      return;
    }

    const realSelectedNodeIds = filterRealCrawlNodeIds(selectedNodeIds);
    if (realSelectedNodeIds.length === 0) {
      return;
    }

    setIsSelectionSubmitting(true);
    updateCrawlSelectionMutation.mutate(
      {
        databaseId,
        crawlId: selectionCrawl.id,
        selectedNodeIds: realSelectedNodeIds,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.crawlSelectionSavedTitle'),
            description: translate('knowledge.crawlSelectionSavedDescription'),
          });
          closeCrawlSelection();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.crawlSelectionSaveError'),
          );
        },
        onSettled: () => {
          setIsSelectionSubmitting(false);
        },
      },
    );
  };

  const confirmSelection = () => {
    if (isAwaitingSelectionStart) {
      runSelectedCrawl();
      return;
    }
    saveSelection();
  };

  return {
    selectionCrawl,
    selectionNodes,
    selectedNodeIds,
    setSelectedNodeIds,
    isSelectionLoading: crawlUrlNodesQuery.isLoading,
    isSelectionSubmitting,
    isAwaitingSelectionStart,
    isScheduledAwaitingSelection,
    openSelection,
    handleCloseSelection,
    confirmSelection,
  };
}
